import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  insufficientCreditsResponseSchema,
  runAlreadyRunningResponseSchema,
  voicesNotConfiguredResponseSchema,
  type GenerationScope,
} from '@tekvoice/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, api } from '@/lib/api';

const number = new Intl.NumberFormat();

interface PendingGeneration {
  scope: GenerationScope;
  label: string;
}

interface GenerationFlow {
  /** Ask for confirmation, showing the live credit estimate first (SPEC §6). */
  request: (scope: GenerationScope, label: string) => void;
  dialog: React.JSX.Element;
  isStarting: boolean;
}

/**
 * SPEC §6/§11 — every generate goes through the same estimate → confirm → start
 * path, whether it is triggered from the batch header or the bulk bar.
 */
export function useGenerationFlow(batchId: string, onStarted: () => void): GenerationFlow {
  const [pending, setPending] = React.useState<PendingGeneration | null>(null);

  const estimate = useQuery({
    queryKey: ['batch', batchId, 'estimate', JSON.stringify(pending?.scope ?? null)],
    queryFn: () => api.batches.estimate(batchId, pending?.scope ?? 'all'),
    enabled: pending !== null,
    staleTime: 0,
  });

  const start = useMutation({
    mutationFn: (scope: GenerationScope) => api.batches.generate(batchId, scope),
    onSuccess: (result) => {
      toast.success(
        `Generating ${result.assetsQueued} clip(s) — about ${number.format(result.estimatedCredits)} credits`,
      );
      setPending(null);
      onStarted();
    },
    onError: (error: Error) => {
      if (error instanceof ApiError) {
        const credits = insufficientCreditsResponseSchema.safeParse(error.body);
        if (credits.success) {
          toast.error(
            `Not enough credits — this run needs ${number.format(credits.data.estimatedCredits)} but only ${number.format(credits.data.remainingCredits)} remain.`,
          );
          return;
        }
        const voices = voicesNotConfiguredResponseSchema.safeParse(error.body);
        if (voices.success) {
          toast.error('Select a voice for both languages in Settings → Voices & models first.');
          return;
        }
        const running = runAlreadyRunningResponseSchema.safeParse(error.body);
        if (running.success) {
          toast.error('A generation run is already in progress for this batch.');
          return;
        }
      }
      toast.error(error.message);
    },
  });

  const data = estimate.data;
  const overBudget = data?.sufficient === false;
  const nothingToDo = data?.assetsQueued === 0;

  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) setPending(null);
      }}
      title={`Generate — ${pending?.label ?? ''}`}
      description="Credits are spent as soon as the run starts."
      confirmLabel="Start generating"
      pending={start.isPending}
      confirmDisabled={estimate.isLoading || nothingToDo || overBudget}
      onConfirm={() => {
        if (pending) start.mutate(pending.scope);
      }}
    >
      <div className="space-y-1 text-sm">
        {estimate.isLoading ? (
          <p className="text-muted-foreground">Estimating…</p>
        ) : estimate.isError ? (
          <p className="text-destructive">Could not estimate this run.</p>
        ) : data ? (
          <>
            <div className="flex justify-between">
              <span>Clips to generate</span>
              <span className="tabular-nums">{number.format(data.assetsQueued)}</span>
            </div>
            <div className="flex justify-between">
              <span>Characters</span>
              <span className="tabular-nums">{number.format(data.characters)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Estimated credits</span>
              <span className="tabular-nums">{number.format(data.estimatedCredits)}</span>
            </div>
            <div className="flex justify-between">
              <span>Remaining credits</span>
              <span className="tabular-nums">
                {data.remainingCredits === null ? 'unknown' : number.format(data.remainingCredits)}
              </span>
            </div>
            {overBudget ? (
              <p className="pt-2 font-medium text-destructive">
                Over budget — top up the ElevenLabs balance before running this.
              </p>
            ) : null}
            {nothingToDo ? (
              <p className="pt-2 text-muted-foreground">Nothing to generate for this selection.</p>
            ) : null}
            {data.lookupError ? (
              <p className="pt-2 text-muted-foreground">
                Live balance unavailable: {data.lookupError}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </ConfirmDialog>
  );

  return { request: (scope, label) => setPending({ scope, label }), dialog, isStarting: start.isPending };
}
